/* eslint-disable max-lines */
import { A } from '@adornis/base/env-info.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/chemistry/elements/components/x-toggle-content.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/dragdrop/x-drag-drop.js';
import '@adornis/file-utils/client/x-file-input.js';
import { StreamableFile } from '@adornis/file-utils/db/streamables.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/forms/x-input.js';
import '@adornis/forms/x-toggle-button.js';
import '@adornis/popover/x-dropdown-selection.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import '@adornis/writer/x-writer.js';
import {
  CONVERSATION_TYPES,
  OsceCategoriesByExamType,
  prettyConversationType,
  prettyOsceCategory,
  type OSCECategory,
  type TaskType,
} from '@ils/static/enums.js';
import { DEFAULT_TEXT_DURATION, DEFAULT_VOICE_DURATION } from '@ils/static/values.js';
import type { brandDict } from '@ils/translation/translation.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { of, type Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import '../../../src/client/components/ils-tinymce.js'; // TODO this crosses the module boundary and has to be refactored!
import { ExamDay } from '../../../src/db/examdays.js'; // TODO this crosses the module boundary and has to be refactored!
import { archiveOSCE, getOSCEsByType } from '../api/osce.js';
import { Instruction } from '../db/instruction.js';
import { InstructionCategory } from '../db/instructionCategory.js';
import { OSCE } from '../db/osce.js';

const writerCommands = [
  'bold',
  'italic',
  'insertImage',
  'formatBlockH1',
  'formatBlockH2',
  'formatBlockH3',
  'underline',
  'strikeThrough',
  'removeFormat',
  'createLink',
  'insertOrderedList',
  'insertUnorderedList',
];

@customElement('ils-editor-osce')
export class ILSEditorOSCE extends ChemistryLitElement {
  override styles() {
    return [
      {
        ':host': {
          flex: '1',
          display: 'flex',
          overflow: 'auto',
        },
      },
    ];
  }
  @state() private readonly branding = new TranslationController<brandDict>(this);

  @property({ attribute: 'osce-id' }) osceID = new RXController(this, '');

  @state() public osce = new RXController(
    this,
    this.osceID.observable.pipe(
      switchMap(osceID =>
        osceID
          ? subscribeByID(
              OSCE,
              osceID,
            )({
              _id: 1,
              osceType: 1,
              examType: 1,
              ratingConfigName: 1,
              isRatedAtLeastOnce: 1,
              taskType: 1,
              enableVideo: 1,
              files: 1,
              text: {
                shortDescription: 1,
                sceneDescriptionTrainee: 1,
                taskDescriptionTrainee: 1,
                sceneDescriptionTester: 1,
              },
              instructionCategories: { description: 1, instructions: { question: 1, description: 1 } },
              title: 1,
              titleTrainee: 1,
              conversationType: 1,
              startButtonDuration: 1,
              durationInMinutes: 1,
              videoFileID: 1,
              createdAt: 1,
              archivedAt: 1,
            })
          : of(undefined),
      ),
    ),
  );
  @state() public osceChanging = new RXController<Observable<OSCE>, OSCE>(
    this,
    this.osce.observable.pipe(
      filter(Boolean),
      switchMap(osce => osce.whenChanging),
    ),
  );
  @state() private readonly _examdayUsages = new RXController(
    this,
    this.osceID.observable.pipe(
      switchMap(osceID =>
        osceID ? ExamDay.getExamDaysWithOsce(osceID)({ _class: 1, _id: 1, name: 1 }) : of(undefined),
      ),
    ),
  );
  @state() private readonly _similarOsces = new RXController(
    this,
    this.osce.observable.pipe(
      switchMap(osce =>
        osce
          ? getOSCEsByType(osce.osceType)({ title: 1, instructionCategories: InstructionCategory.allFields }).then(
              osces => osces.filter(x => x !== osce),
            )
          : of([]),
      ),
    ),
  );
  @state() private readonly _form = new FormController(this, this.osce.observable, {
    success: async osce => {
      if (osce.taskType === 'text') osce.conversationType = null;
      await osce
        .save()
        .then(() => XSnackbar.show(`${this.branding.translate('osce') as string} wurde gespeichert.`))
        .catch(e => XDialog.alert(Array.isArray(e.errors) ? e.errors[0].message : e.message));
    },
    error: error => XDialog.alert(error.message),
    disabledFields: this.osce.observable.pipe(
      map(osce => (osce?.isRatedAtLeastOnce ? { instructionCategories: 1 } : {})),
    ),
  });

  override render() {
    const maxWidth = `${1000 + 32}` + 'px';
    if (!this.osce.value) return '';
    return html`
      <x-flex padding="md" ${css({ width: '100%', maxWidth })}>
        <x-flex space="md" ${css({ paddingBottom: this.spacing.lg })}>
          <x-flex space="md">
            <x-flex horizontal crossaxis-center space="md">
              <x-flex flex horizontal crossaxis-center space="sm">
                <x-button
                  mode="icon"
                  icon="arrow_back"
                  @click=${() => {
                    this.osce.value?.revert();
                    try {
                      window.history.back();
                    } catch {
                      goTo({ path: '/start/editor-osce', searchParams: NO_CHANGE });
                    }
                  }}
                ></x-button>
                <x-h2> ${this.osce.value.examType} ${this.branding.translate('osce_editor', 'editor_title')} </x-h2>
              </x-flex>
              <x-button mode="outline" @click="${() => this.archiveOSCE(this.osce.value!)}">
                ${this.branding.translate('osce')} archivieren
              </x-button>
              <x-button @click=${(e: MouseEvent) => this._form.validate({ mouseEvent: e })}>
                Änderungen speichern
              </x-button>
            </x-flex>
            ${this._usageInExamDays()}
            <x-input flex placeholder="Titel" ${this._form.field('title')}></x-input>
            <x-input flex placeholder="Titel Prüfling" ${this._form.field('titleTrainee')}></x-input>
            <x-dropdown-selection
              ${this._form.field('osceType')}
              placeholder="OSCE Typ"
              .value=${this.osce.value.osceType}
              .selectables=${OsceCategoriesByExamType[this.osce.value.examType]}
              .renderString=${(category: OSCECategory | undefined) => (category ? prettyOsceCategory(category) : '')}
            ></x-dropdown-selection>
            <x-flex space="sm">
              ${this.osce.value.taskType === 'voice'
                ? html`
                    <x-flex space="sm" padding="md">
                      <x-text bold> Was ist die maximale Vorbereitungszeit? </x-text>
                      <x-text tone="subtle">
                        Einlesezeit, die der Prüfling getrennt vom Schauspieler hat um die Aufgabenstellung in Ruhe zu
                        lesen. Anschließend werden Schauspieler und Prüfling wieder automatisch miteinander verbunden.
                      </x-text>
                    </x-flex>
                  `
                : nothing}
              <x-flex horizontal crossaxis-end crossaxis-end space="sm">
                <x-dropdown-selection
                  ${this._form.field('taskType')}
                  placeholder="Stationstyp"
                  ${css({ minWidth: '20ch' })}
                  .value=${this.osce.value.taskType}
                  .selectables=${['voice', 'text']}
                  .renderString=${(taskType: string) => {
                    if (taskType === 'voice') return 'Konversation';
                    else if (taskType === 'text') return 'Text';
                  }}
                  @value-picked=${(e: ValueEvent<TaskType>) => {
                    /*
                    We only adjust the value of durationInMinutes when it matches the default value
                    corresponding to the previous task type.
                    */
                    if (
                      !this._form.document?.durationInMinutes ||
                      this._form.document.durationInMinutes ===
                        (e.detail.value === 'text'
                          ? // Previous value was 'voice'
                            DEFAULT_VOICE_DURATION
                          : // Previous value was 'text'
                            DEFAULT_TEXT_DURATION)
                    )
                      this._form.pickValueForField(
                        ['durationInMinutes'],
                        e.detail.value === 'voice' ? DEFAULT_VOICE_DURATION : DEFAULT_TEXT_DURATION,
                      );
                  }}
                ></x-dropdown-selection>
                ${this.osce.value.taskType === 'voice'
                  ? html`
                      <x-dropdown-selection
                        placeholder="Konversationstyp"
                        .selectables="${CONVERSATION_TYPES}"
                        .renderString=${(convType: any) => {
                          return prettyConversationType(convType);
                        }}
                        ${this._form.field('conversationType')}
                      >
                      </x-dropdown-selection>
                      <x-input
                        flex
                        placeholder="Maximale Vorbereitungszeit (in Sekunden)"
                        type="number"
                        ${this._form.field('startButtonDuration')}
                      ></x-input>
                      <x-toggle-button
                        ${css({ alignSelf: 'flex-end', marginBottom: this.spacing.sm })}
                        ${this._form.field('enableVideo')}
                        label="Video aktivieren:"
                        reverse
                        .value=${this.osce.value!.enableVideo}
                        .editable=${this.osce.value.taskType === 'voice'}
                      ></x-toggle-button>
                    `
                  : ''}
                ${this.osce.value.taskType === 'text'
                  ? html`
                      <x-file-input
                        .fileEntity=${StreamableFile}
                        flex
                        accept=".mp4"
                        placeholder="Intro Video"
                        ${this._form.field('videoFileID')}
                      ></x-file-input>
                    `
                  : nothing}
              </x-flex>
            </x-flex>
            <x-flex horizontal crossaxis-end space="sm">
              <x-input
                flex
                placeholder="Dauer (in Minuten)"
                type="number"
                ${this._form.field('durationInMinutes')}
              ></x-input>
            </x-flex>
            <x-flex space="sm">
              <x-text tone="subtle" ${css({ marginLeft: this.spacing.md })}>
                Kurzbeschreibung (in der Pause zu sehen)
              </x-text>
              <ils-tinymce
                xattach-toolbar
                .commands=${writerCommands}
                ${this._form.field('text', 'shortDescription')}
                flex
                placeholder="Kurzbeschreibung (in der Pause zu sehen)"
                .value=${this.osce.value.text?.shortDescription}
              ></ils-tinymce>
            </x-flex>
            <x-flex space="sm">
              <x-text tone="subtle" ${css({ marginLeft: this.spacing.md })}> Fallbeschreibung (Prüfling) </x-text>
              <ils-tinymce
                ${this._form.field('text', 'sceneDescriptionTrainee')}
                flex
                placeholder="Fallbeschreibung (Prüfling)"
                .value="${this.osce.value.text?.sceneDescriptionTrainee}"
              ></ils-tinymce>
            </x-flex>
            <x-flex space="sm">
              <x-text tone="subtle" ${css({ marginLeft: this.spacing.md })}> Aufgabentext (Prüfling) </x-text>
              <ils-tinymce
                xattach-toolbar
                .commands=${writerCommands}
                ${this._form.field('text', 'taskDescriptionTrainee')}
                flex
                placeholder="Aufgabentext (Prüfling)"
                .editable="${true}"
                .value="${this.osce.value.text?.taskDescriptionTrainee}"
              ></ils-tinymce>
            </x-flex>
            <x-flex space="sm">
              <x-text tone="subtle" ${css({ marginLeft: this.spacing.md })}> Szenenbeschreibung (Schauspieler) </x-text>
              <ils-tinymce
                xattach-toolbar
                .commands=${writerCommands}
                ${this._form.field('text', 'sceneDescriptionTester')}
                flex
                placeholder="Szenenbeschreibung (Schauspieler)"
                .editable="${true}"
                .value="${this.osce.value.text?.sceneDescriptionTester}"
              ></ils-tinymce>
            </x-flex>
          </x-flex>
          <x-flex space="md">
            <x-flex horizontal crossaxis-center space="md" space-between>
              <x-h2>Bewertungskriterien</x-h2>
              ${this._similarOsces.value?.length && !this.osce.value.isRatedAtLeastOnce
                ? html`
                    <x-dropdown-selection
                      placeholder="Bewertungskriterien kopieren von..."
                      .search=${(search?: string) =>
                        search
                          ? this._similarOsces.value
                              ?.filter(item =>
                                new RegExp(search.replace(/([^a-zA-Z0-9\s])/g, '\\$1'), 'i').test(item.title),
                              )
                              .sort((a, b) => a.title.localeCompare(b.title)) ?? []
                          : this._similarOsces.value?.sort((a, b) => a.title.localeCompare(b.title)) ?? []}
                      .renderString=${(item?: OSCE) => item?.title || ''}
                      @value-picked=${async (e: CustomEvent) => {
                        if (
                          !e.detail.value ||
                          !this.osce.value ||
                          (this.osce.value.instructionCategories.length &&
                            !(await XDialog.confirm(
                              'Möchtest du die aktuellen Bewertungskriterien unwiderruflich überschreiben?',
                            )))
                        )
                          return;

                        this.osce.value.instructionCategories = e.detail.value.instructionCategories.map(
                          instructionCategory => {
                            instructionCategory._id = A.getGloballyUniqueID();
                            instructionCategory.instructions = instructionCategory.instructions.map(instruction => {
                              instruction._id = A.getGloballyUniqueID();
                              return instruction;
                            });

                            return instructionCategory;
                          },
                        );
                      }}
                    ></x-dropdown-selection>
                  `
                : nothing}
            </x-flex>
            ${this.osce.value.isRatedAtLeastOnce
              ? html`
                  <x-basic-card
                    horizontal
                    crossaxis-center
                    space="md"
                    padding="md"
                    ${css({ background: this.colors.tone.warning })}
                  >
                    <x-icon> warning </x-icon>
                    <x-text>
                      Für diesen OSCE existieren bereits Bewertungen, daher können die Bewertungskriterien nicht mehr
                      angepasst werden
                    </x-text>
                  </x-basic-card>
                `
              : nothing}
            ${repeat(
              this.osce.value.instructionCategories,
              i => i._id,
              (instructCat: InstructionCategory, idxCat: number) => {
                return html`
                  <x-flex space="sm">
                    <x-h4>Kategorie ${idxCat + 1}:</x-h4>
                    <x-flex horizontal crossaxis-end space="sm" ${css({ maxWidth })}>
                      <x-input
                        placeholder="Titel"
                        placeholder-mode="hide"
                        flex
                        ${this._form.field('instructionCategories', idxCat, 'description')}
                      ></x-input>
                      ${this.osce.value?.isRatedAtLeastOnce
                        ? nothing
                        : html`
                            <x-button
                              mode="icon"
                              icon="delete"
                              ${css({ marginBottom: '8px' })}
                              @click="${() => this.removeInstructionCategory(this.osce.value!, idxCat)}"
                            ></x-button>
                          `}
                    </x-flex>
                    <x-drag-drop
                      drag-mode="column"
                      child-selector="[item]"
                      allow-from="[handle]"
                      @resorted=${(e: CustomEvent<{ start: number; end: number; order: Array<string | null> }>) => {
                        if (e.detail.start === e.detail.end) return;
                        const tmp = instructCat.instructions.splice(e.detail.start, 1);
                        instructCat.instructions.splice(e.detail.end, 0, tmp[0]!);
                        this.requestUpdate();
                      }}
                    >
                      <x-flex space="md" ${css({ width: '100%' })}>
                        ${repeat(
                          instructCat.instructions,
                          i => i._id,
                          (instruction, idx) => {
                            return html`
                              <x-flex
                                horizontal
                                space="sm"
                                item
                                key="${idx}"
                                ${css({
                                  paddingLeft: this.spacing.lg,
                                  borderBottom: `1px solid ${this.colors.neutral.shade(200)}`,
                                })}
                              >
                                <x-flex center>
                                  ${this.osce.value?.isRatedAtLeastOnce
                                    ? nothing
                                    : html`
                                        <x-icon
                                          handle
                                          ${css({ color: this.colors.neutral.shade(700), fontSize: '1.2em' })}
                                        >
                                          drag_indicator
                                        </x-icon>
                                      `}
                                </x-flex>
                                <x-flex space="sm" flex>
                                  <x-flex
                                    horizontal
                                    crossaxis-end
                                    space="sm"
                                    ${css({ maxWidth: `calc(${maxWidth} - 32px)` })}
                                  >
                                    ${this.osce.value?.isRatedAtLeastOnce
                                      ? html`
                                          <x-basic-card padding="md" flex>
                                            <x-text> ${unsafeHTML(instruction.question)} </x-text>
                                          </x-basic-card>
                                        `
                                      : html`
                                          <x-writer
                                            xattach-toolbar
                                            .commands=${writerCommands}
                                            flex
                                            placeholder="Frage ${idx + 1}"
                                            ${this._form.field(
                                              'instructionCategories',
                                              idxCat,
                                              'instructions',
                                              idx,
                                              'question',
                                            )}
                                          ></x-writer>
                                          <x-button
                                            mode="icon"
                                            icon="delete"
                                            ${css({ marginBottom: '8px' })}
                                            @click="${() => this.removeQuestion(this.osce.value!, idxCat, idx)}"
                                          ></x-button>
                                        `}
                                  </x-flex>
                                  ${this.osce.value?.isRatedAtLeastOnce
                                    ? html`
                                        <x-basic-card padding="md" flex ${css({ marginBottom: this.spacing.md })}>
                                          <x-text> ${unsafeHTML(instruction.description)} </x-text>
                                        </x-basic-card>
                                      `
                                    : html`
                                        <x-writer
                                          xattach-toolbar
                                          .commands=${writerCommands}
                                          placeholder="Beschreibung"
                                          ${this._form.field(
                                            'instructionCategories',
                                            idxCat,
                                            'instructions',
                                            idx,
                                            'description',
                                          )}
                                          ${css({ paddingBottom: this.spacing.md, paddingRight: '37px' })}
                                        ></x-writer>
                                      `}
                                </x-flex>
                              </x-flex>
                              ${!this.osce.value?.isRatedAtLeastOnce && idx === instructCat.instructions.length - 1
                                ? html`
                                    <x-flex horizontal start ${css({ paddingLeft: this.spacing.lg })}>
                                      <x-button
                                        mode="outline"
                                        @click="${() => this.addNewQuestion(this.osce.value!, idxCat)}"
                                      >
                                        Neue Frage hinzufügen
                                      </x-button>
                                    </x-flex>
                                  `
                                : ''}
                            `;
                          },
                        )}
                      </x-flex>
                    </x-drag-drop>
                  </x-flex>
                `;
              },
            )}
            ${!this.osce.value.isRatedAtLeastOnce
              ? html` <x-flex horizontal start></x-flex>
                    <x-button mode="outline" @click="${() => this.addNewInstructionCategory(this.osce.value!)}">
                      Neue Kategorie hinzufügen
                    </x-button>
                  </x-flex> `
              : nothing}
          </x-flex>
          <x-flex horizontal end>
            <x-button @click=${(e: MouseEvent) => this._form.validate({ mouseEvent: e })}>
              Änderungen speichern
            </x-button>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }
  private addNewInstructionCategory(osce: OSCE) {
    osce.instructionCategories.push(
      new InstructionCategory({ description: '', instructions: [new Instruction({ question: '', description: '' })] }),
    );
    // osce.save();
  }

  private removeInstructionCategory(osce: OSCE, index: number) {
    osce.instructionCategories.splice(index, 1);
    // osce.save();
  }

  private addNewQuestion(osce: OSCE, categoryIndex: number) {
    osce.instructionCategories[categoryIndex]!.instructions.push(new Instruction({ question: '', description: '' }));
    this.requestUpdate();
    // osce.save();
  }

  private removeQuestion(osce: OSCE, categoryIndex: number, index: number) {
    osce.instructionCategories[categoryIndex]!.instructions.splice(index, 1);
    this.requestUpdate();
    // osce.save();
  }

  private async archiveOSCE(osce: OSCE) {
    const branding = this.branding.translate('osce') as string;

    if (
      await XDialog.confirm(
        html` <p>
          Möchten Sie den ${branding} "${osce.title}" wirklich archivieren?<br />
          Archivierte ${branding} können nur über die Datenbank wiederhergestellt werden!
        </p>`,
        {
          highlightDecline: true,
        },
      )
    ) {
      try {
        await archiveOSCE(osce._id)();
        goTo({ path: '/start/editor-osce', searchParams: NO_CHANGE });
      } catch (error: any) {
        void XDialog.alert(error.message);
      }
    }
  }

  private _usageInExamDays() {
    if (!this._examdayUsages.value?.length) return nothing;
    return html`
      <x-toggle-content label="${this.branding.translate('osce_editor', 'usages')}">
        <x-flex space="sm">
          ${repeat(
            this._examdayUsages.value,
            examday =>
              html`
                <x-flex horizontal crossaxis-center space-between space="md">
                  <x-text> ${examday.name} </x-text>
                  <x-button mode="icon" icon="edit" href="/start/editor-exam/${examday._id}"></x-button>
                </x-flex>
              `,
          )}
        </x-flex>
      </x-toggle-content>
    `;
  }
}
